import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import StandardModal from './StandardModal';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  AlertCircle, Package, User, Phone, Mail, Users,
  MapPin, Calendar, DollarSign, Gift, FileText, CreditCard,
  Loader2, Check, Clock, Send, Anchor, AlertTriangle, ChevronDown, Plus
} from 'lucide-react';
import { PrimaryButton, SecondaryButton } from './Buttons';
import TimeSlotSelector from './TimeSlotSelector';
import PaymentMethodSelector from './PaymentMethodSelector';
import { toast } from 'sonner';
import api from '../utils/api';
import { TIMEZONES } from '../utils/timezones';

/**
 * Edit Booking Modal - Clean, organized layout
 * Tabs: Trip | Customer | Assignment | Payment | Extras
 */

const EMPTY_ARRAY = [];

// Duration options in minutes
const DURATION_OPTIONS = [
  { value: 30, label: '30 min' },
  { value: 60, label: '1 hr' },
  { value: 90, label: '1.5 hr' },
  { value: 120, label: '2 hr' },
  { value: 150, label: '2.5 hr' },
  { value: 180, label: '3 hr' },
  { value: 240, label: '4 hr' },
  { value: 300, label: '5 hr' },
  { value: 360, label: '6 hr' },
  { value: 480, label: '8 hr' },
];

const EditBookingModal = ({
  isOpen,
  onClose,
  trip,
  assets = EMPTY_ARRAY,
  teamMembers = EMPTY_ARRAY,
  roles = EMPTY_ARRAY,
  allBookings = EMPTY_ARRAY,
  onSave,
  onGeneratePaymentLink,
  timeFormat,
  services = EMPTY_ARRAY,
  addOns = EMPTY_ARRAY
}) => {
  const [formData, setFormData] = useState({
    // Schedule
    date: '',
    startTime: '',
    duration: 60,
    timezone: '',
    // Customer
    customerName: '',
    customerPhone: '',
    customerEmail: '',
    partySize: 1,
    emergencyContactNumber: '',
    notes: '',
    // Assignment
    assetId: '',
    teamAssignments: [],
    // Location
    city: '',
    country: '',
    pickupLocation: '',
    // Status
    paymentStatus: 'unpaid',
    checkedIn: false,
    // Add-ons
    selectedAddOns: [],
  });
  
  const [originalData, setOriginalData] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [serviceAddOns, setServiceAddOns] = useState([]);
  const [priceBreakdown, setPriceBreakdown] = useState({
    original: 0,
    current: 0,
    difference: 0
  });
  const [saving, setSaving] = useState(false);
  const [generatingPaymentLink, setGeneratingPaymentLink] = useState(false);
  const [activeTab, setActiveTab] = useState('trip');
  
  // Add Extras state
  const [newExtras, setNewExtras] = useState([]); // Add-ons selected to add
  const [extrasPaymentMethod, setExtrasPaymentMethod] = useState('');
  const [extrasCurrency, setExtrasCurrency] = useState('');
  const [processingExtras, setProcessingExtras] = useState(false);
  const [customExtraAmount, setCustomExtraAmount] = useState(''); // For custom payment amount
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  
  const originalPriceRef = useRef(0);

  // Load supported currencies for multi-currency checkout
  useEffect(() => {
    const loadSupportedCurrencies = async () => {
      try {
        const orgCountry = localStorage.getItem('orgCountry') || 'US';
        const response = await api.get(`/admin/supported-currencies?org_country=${orgCountry}`);
        if (response.data?.currencies) {
          const sorted = [...response.data.currencies].sort((a, b) => {
            if (a.tier === 'guaranteed' && b.tier !== 'guaranteed') return -1;
            if (a.tier !== 'guaranteed' && b.tier === 'guaranteed') return 1;
            return 0;
          });
          setSupportedCurrencies(sorted);
        }
      } catch (error) {
        console.error('Failed to load supported currencies:', error);
      }
    };
    loadSupportedCurrencies();
  }, []);

  // Initialize form data when trip changes
  useEffect(() => {
    if (trip) {
      // Check if the assigned asset has team requirements
      let initialTeamAssignments = trip.teamAssignments || trip.team_assignments || [];
      
      // If asset is assigned but no team assignments, populate from asset requirements
      const tripAssetId = trip.assetId || trip.asset_id;
      if (tripAssetId && initialTeamAssignments.length === 0 && assets.length > 0) {
        const selectedAsset = assets.find(b => b.id === tripAssetId);
        if (selectedAsset?.teamRequirements && selectedAsset.teamRequirements.length > 0) {
          const autoTeamSlots = [];
          selectedAsset.teamRequirements.forEach(req => {
            const currentRole = roles?.find(r => r.id === req.roleId);
            const currentRoleName = currentRole?.name || req.roleName || 'Team';
            for (let i = 0; i < req.quantity; i++) {
              autoTeamSlots.push({
                teamId: '',
                teamName: '',
                roleId: req.roleId,
                roleName: currentRoleName
              });
            }
          });
          initialTeamAssignments = autoTeamSlots;
        }
      }
      
      const initialData = {
        // Handle both snake_case (from Schedule API) and camelCase (from Bookings API)
        date: trip.date || trip.bookingDate || trip.booking_date || '',
        startTime: trip.startTime || trip.start_time || '',
        duration: trip.duration || trip.durationMinutes || trip.duration_minutes || 60,
        timezone: trip.timezone || localStorage.getItem('orgTimezone') || 'America/Chicago',
        customerName: trip.customerName || trip.customer_name || '',
        customerPhone: trip.customerPhone || trip.customer_phone || '',
        customerEmail: trip.customerEmail || trip.customer_email || '',
        partySize: trip.partySize || trip.party_size || 1,
        emergencyContactNumber: trip.emergencyContactNumber || trip.emergency_contact_number || '',
        notes: trip.notes || trip.internalNotes || trip.internal_notes || '',
        assetId: trip.assetId || trip.asset_id || '',
        teamAssignments: initialTeamAssignments,
        city: trip.city || '',
        country: trip.country || '',
        pickupLocation: trip.pickupLocation || trip.meeting_point || trip.pickup_location || trip.locationDetails || '',
        paymentStatus: trip.paymentStatus || trip.payment_status || 'unpaid',
        checkedIn: trip.checkedIn || trip.checked_in || false,
        selectedAddOns: trip.selectedAddOns || trip.selected_add_ons || trip.add_ons || [],
      };
      setFormData(initialData);
      setOriginalData(initialData);
      setActiveTab('trip');
      
      const originalTotal = trip.paidAmount || trip.paid_amount || trip.totalAmount || trip.total_price || trip.servicePrice || 0;
      originalPriceRef.current = originalTotal;
      setPriceBreakdown({
        original: originalTotal,
        current: originalTotal,
        difference: 0
      });
      
      const tripServiceId = trip.serviceId || trip.service_id;
      if (tripServiceId && services.length > 0) {
        const service = services.find(s => s.id === tripServiceId);
        if (service?.addOns) {
          setServiceAddOns(service.addOns);
        }
      }
    }
  }, [trip, services, assets, roles]);

  // Calculate price when add-ons change
  useEffect(() => {
    if (!originalData) return;
    
    const originalAddOnsTotal = (originalData.selectedAddOns || []).reduce(
      (sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0
    );
    
    const currentAddOnsTotal = (formData.selectedAddOns || []).reduce(
      (sum, addon) => sum + ((addon.price || 0) * (addon.quantity || 1)), 0
    );
    
    const basePrice = (originalPriceRef.current || 0) - originalAddOnsTotal;
    const newTotal = basePrice + currentAddOnsTotal;
    const difference = currentAddOnsTotal - originalAddOnsTotal;
    
    setPriceBreakdown({
      original: originalPriceRef.current,
      current: newTotal,
      difference: difference
    });
  }, [formData.selectedAddOns, originalData]);

  // Helper functions
  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  const checkTimeOverlap = (trip1Start, trip1Duration, trip2Start, trip2Duration) => {
    const start1 = timeToMinutes(trip1Start);
    const end1 = start1 + trip1Duration;
    const start2 = timeToMinutes(trip2Start);
    const end2 = start2 + trip2Duration;
    return start1 < end2 && end1 > start2;
  };

  const calculateEndTime = (startTime, duration) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + duration;
    const hours = Math.floor(endMinutes / 60) % 24;
    const mins = endMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Check for conflicts
  useEffect(() => {
    if (!formData.startTime || !formData.date) return;

    const detectedConflicts = [];
    const availableSuggestions = [];

    if (formData.assetId && formData.assetId !== 'unassigned') {
      const assetConflicts = allBookings.filter(t =>
        t.id !== trip?.id &&
        t.date === formData.date &&
        t.assetId === formData.assetId &&
        checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
      );

      if (assetConflicts.length > 0) {
        const asset = assets.find(b => b.id === formData.assetId);
        detectedConflicts.push({
          type: 'asset',
          message: `${asset?.name} is booked ${assetConflicts[0].startTime} - ${calculateEndTime(assetConflicts[0].startTime, assetConflicts[0].duration)}`
        });

        const availableAssets = assets.filter(b =>
          b.active &&
          !allBookings.some(t =>
            t.id !== trip?.id &&
            t.date === formData.date &&
            t.assetId === b.id &&
            checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
          )
        );

        if (availableAssets.length > 0) {
          availableSuggestions.push({
            type: 'asset',
            items: availableAssets.slice(0, 3).map(v => ({ id: v.id, name: v.name }))
          });
        }
      }
    }

    formData.teamAssignments.forEach((assignment) => {
      if (assignment.teamId) {
        const teamConflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.teamAssignments?.some(a => a.teamId === assignment.teamId) &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );

        if (teamConflicts.length > 0) {
          const team = teamMembers.find(c => c.id === assignment.teamId);
          detectedConflicts.push({
            type: 'team',
            message: `${team?.name} is booked ${teamConflicts[0].startTime} - ${calculateEndTime(teamConflicts[0].startTime, teamConflicts[0].duration)}`
          });
        }
      }
    });

    setConflicts(detectedConflicts);
    setSuggestions(availableSuggestions);
  }, [formData.startTime, formData.date, formData.assetId, formData.teamAssignments, formData.duration, allBookings, trip, assets, teamMembers]);

  // Handle asset change - auto-template team slots
  const handleAssetChange = (assetId) => {
    setFormData(prev => ({ ...prev, assetId: assetId }));

    if (assetId !== 'unassigned' && assetId) {
      const selectedAsset = assets.find(b => b.id === assetId);
      if (selectedAsset?.teamRequirements) {
        const autoTeamSlots = [];
        selectedAsset.teamRequirements.forEach(req => {
          const currentRole = roles?.find(r => r.id === req.roleId);
          const currentRoleName = currentRole?.name || req.roleName || 'Team';
          for (let i = 0; i < req.quantity; i++) {
            autoTeamSlots.push({
              teamId: '',
              teamName: '',
              roleId: req.roleId,
              roleName: currentRoleName
            });
          }
        });
        setFormData(prev => ({ ...prev, teamAssignments: autoTeamSlots }));
      }
    }
  };

  // Team assignment handler
  const handleTeamChange = (idx, value) => {
    const newAssignments = [...formData.teamAssignments];
    const selectedTeam = teamMembers.find(t => t.id === value);
    newAssignments[idx] = {
      ...newAssignments[idx],
      teamId: value === 'unassigned' ? '' : value,
      teamName: selectedTeam?.name || ''
    };
    setFormData(prev => ({ ...prev, teamAssignments: newAssignments }));
  };

  // Add-on handlers
  const handleAddOnToggle = (addon, checked) => {
    setFormData(prev => {
      if (checked) {
        return {
          ...prev,
          selectedAddOns: [...prev.selectedAddOns, { ...addon, quantity: 1 }]
        };
      } else {
        return {
          ...prev,
          selectedAddOns: prev.selectedAddOns.filter(a => a.id !== addon.id && a.name !== addon.name)
        };
      }
    });
  };

  const handleAddOnQuantityChange = (addonId, quantity) => {
    setFormData(prev => ({
      ...prev,
      selectedAddOns: prev.selectedAddOns.map(addon =>
        (addon.id === addonId || addon.name === addonId) 
          ? { ...addon, quantity: Math.max(1, quantity) }
          : addon
      )
    }));
  };

  const isAddOnSelected = (addon) => {
    return formData.selectedAddOns.some(a => a.id === addon.id || a.name === addon.name);
  };

  const getAddOnQuantity = (addon) => {
    const selected = formData.selectedAddOns.find(a => a.id === addon.id || a.name === addon.name);
    return selected?.quantity || 1;
  };

  // Save handler
  const handleSave = async () => {
    if (conflicts.length > 0) {
      const confirmSave = window.confirm(
        `Warning: ${conflicts.length} conflict(s) detected:\n${conflicts.map(c => c.message).join('\n')}\n\nDo you want to save anyway?`
      );
      if (!confirmSave) return;
    }

    setSaving(true);
    
    try {
      const priceDiff = priceBreakdown.difference;
      
      if (Math.abs(priceDiff) > 0.01) {
        if (priceDiff > 0) {
          try {
            const chargeResponse = await api.post(`/bookings/${trip.id}/add-charge`, {
              amount: priceDiff,
              description: 'Additional add-ons',
              addOns: formData.selectedAddOns
            });
            
            if (chargeResponse.data.checkoutUrl) {
              toast.info('Redirecting to payment for additional charges...');
              window.location.href = chargeResponse.data.checkoutUrl;
              return;
            } else {
              toast.success(`Additional charge of $${priceDiff.toFixed(2)} processed`);
            }
          } catch (chargeError) {
            console.error('Charge error:', chargeError);
            toast.error('Could not process additional charge. Saving booking without payment update.');
          }
        } else if (priceDiff < 0) {
          toast.info(
            `Price decreased by $${Math.abs(priceDiff).toFixed(2)}. If you need to refund this amount, do so in your Stripe Dashboard.`,
            { duration: 8000 }
          );
        }
      }
      
      await onSave(trip.id, {
        ...formData,
        totalAmount: priceBreakdown.current,
        priceModified: Math.abs(priceDiff) > 0.01,
      });
      
      onClose();
      toast.success('Booking updated!');
    } catch (error) {
      console.error('❌ [EditBookingModal] Save error:', error);
      toast.error('Failed to save booking');
    } finally {
      setSaving(false);
    }
  };

  // Get assets with availability status
  const assetsWithStatus = useMemo(() => {
    return assets.map(asset => {
      let available = true;
      let reason = '';

      if (!asset.active) {
        available = false;
        reason = 'Inactive';
      }

      if (available && formData.startTime && formData.date) {
        const conflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.assetId === asset.id &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );
        if (conflicts.length > 0) {
          available = false;
          reason = `Booked ${conflicts[0].startTime}`;
        }
      }

      return { ...asset, available, reason };
    });
  }, [assets, formData.startTime, formData.date, formData.duration, allBookings, trip?.id]);

  // Get team with availability status
  const getTeamWithStatus = useCallback((roleId) => {
    return teamMembers.map(c => {
      let available = true;
      let reason = '';

      if (roleId && c.roleId !== roleId) {
        available = false;
        const teamRole = roles.find(r => r.id === c.roleId);
        reason = teamRole?.name || 'Different role';
      }

      if (available && formData.startTime && formData.date) {
        const conflicts = allBookings.filter(t =>
          t.id !== trip?.id &&
          t.date === formData.date &&
          t.teamAssignments?.some(a => a.teamId === c.id) &&
          checkTimeOverlap(formData.startTime, formData.duration, t.startTime, t.duration)
        );
        if (conflicts.length > 0) {
          available = false;
          reason = `Booked ${conflicts[0].startTime}`;
        }
      }

      return { ...c, available, reason };
    });
  }, [teamMembers, roles, formData.startTime, formData.date, formData.duration, allBookings, trip?.id]);

  // Guard against invalid trip object
  if (!trip || typeof trip !== 'object') return null;
  
  // Validate trip has required properties (guard against error objects being passed)
  if (trip.error || trip.detail) {
    console.error('EditBookingModal received an error object instead of trip:', trip);
    return null;
  }

  const availableAddOns = serviceAddOns.length > 0 ? serviceAddOns : addOns;
  const selectedAsset = assets.find(a => a.id === formData.assetId);
  const hasExtras = availableAddOns.length > 0 || (trip.customAnswers && Object.keys(trip.customAnswers).length > 0);
  
  // Get fee breakdown - prefer feeBreakdown object for accuracy
  const fb = trip?.feeBreakdown || {};
  
  // Fee breakdown for display - use feeBreakdown object for accuracy
  const breakdown = {
    subtotal: fb.subtotal || trip?.basePrice || trip?.servicePrice || 0,
    addOnsTotal: trip?.addOnsTotal || 0,
    platformFee: fb.platformFee || trip?.platformFee || 0,
    stripeFee: fb.stripeFee || trip?.stripeFee || 0,
    serviceFee: fb.serviceFee || trip?.serviceFee || 0,
    salesTax: fb.salesTax || trip?.salesTax || 0,
    taxRate: fb.taxRate || trip?.taxRate || 0,
    totalFees: fb.totalFees || ((fb.platformFee || 0) + (fb.stripeFee || 0) + (fb.serviceFee || 0)),
  };
  
  // Calculate Total Due = subtotal + fees + tax
  // The stored total_price might be incorrect (missing fees/tax), so we calculate it correctly
  const calculatedTotal = breakdown.subtotal + breakdown.totalFees + breakdown.salesTax;
  
  // Use calculated total if it's larger than stored (means stored is missing fees/tax)
  // Otherwise use stored value (feeBreakdown.grandTotal or totalAmount)
  const storedTotal = fb.grandTotal || trip?.totalAmount || trip?.totalPrice || 0;
  const totalDue = calculatedTotal > storedTotal ? calculatedTotal : storedTotal;
  
  const amountPaid = trip?.paidAmount || trip?.amountPaid || 0;
  const remaining = Math.max(0, totalDue - amountPaid);
  
  // Check if considered "paid" (within 5 cents tolerance)
  const isPaid = remaining <= 0.05;
  
  // Check if this is a past booking
  const isPastBooking = trip?.date && new Date(trip.date) < new Date(new Date().toDateString());

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Booking"
      description={
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="outline" className="font-mono text-xs">{typeof trip.confirmationCode === 'string' ? trip.confirmationCode : (trip.confirmationCode?.toString?.() || '')}</Badge>
          <span className="text-gray-400">•</span>
          <span className="text-gray-600">{typeof trip.serviceName === 'string' ? trip.serviceName : (trip.serviceName || 'Booking')}</span>
        </div>
      }
      size="md"
      footer={
        <div className="flex gap-3 w-full">
          <SecondaryButton onClick={onClose} className="flex-1" disabled={saving}>
            Cancel
          </SecondaryButton>
          <PrimaryButton onClick={handleSave} className="flex-1" disabled={saving}>
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </PrimaryButton>
        </div>
      }
    >
      {/* Conflicts Warning */}
      {conflicts.length > 0 && (
        <div className="p-3 mb-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-orange-800 mb-1">Scheduling Conflict</p>
              {conflicts.map((conflict, idx) => (
                <p key={idx} className="text-xs text-orange-700">• {conflict.message}</p>
              ))}
            </div>
          </div>
          {suggestions.length > 0 && suggestions[0].type === 'asset' && (
            <div className="mt-2 pt-2 border-t border-orange-200">
              <p className="text-xs text-orange-800 mb-1">Available alternatives:</p>
              <div className="flex flex-wrap gap-1">
                {suggestions[0].items.map(item => (
                  <button
                    key={item.id}
                    onClick={() => handleAssetChange(item.id)}
                    className="px-2 py-1 bg-white border border-orange-300 rounded text-xs text-orange-700 hover:bg-orange-100"
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full grid grid-cols-5 gap-1 mb-4 p-1 h-auto">
          <TabsTrigger value="trip" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Trip</TabsTrigger>
          <TabsTrigger value="customer" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Guest</TabsTrigger>
          <TabsTrigger value="assignment" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Assign</TabsTrigger>
          <TabsTrigger value="payment" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Pay</TabsTrigger>
          <TabsTrigger value="addons" className="text-xs sm:text-sm py-2 px-1 sm:px-2 min-h-[36px]">Extras</TabsTrigger>
        </TabsList>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 1: TRIP - Schedule, Location, Guests
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="trip" className="space-y-5 mt-0">
          {/* Schedule Section */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Calendar className="w-4 h-4" />
              Schedule
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="flex flex-col">
                <Label className="text-xs text-gray-500 mb-1 block">Date</Label>
                <Input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                  className="h-11 w-full"
                />
              </div>
              <div className="flex flex-col">
                <Label className="text-xs text-gray-500 mb-1 block">Time</Label>
                <TimeSlotSelector
                  value={formData.startTime}
                  onChange={(time) => setFormData(prev => ({ ...prev, startTime: time }))}
                  className="w-full"
                />
              </div>
              <div className="flex flex-col">
                <Label className="text-xs text-gray-500 mb-1 block">Duration</Label>
                <Select
                  value={String(formData.duration)}
                  onValueChange={(val) => setFormData(prev => ({ ...prev, duration: parseInt(val) }))}
                >
                  <SelectTrigger className="h-11 w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DURATION_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={String(opt.value)}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Timezone</Label>
              <Select
                value={formData.timezone}
                onValueChange={(val) => setFormData(prev => ({ ...prev, timezone: val }))}
              >
                <SelectTrigger className="h-11 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIMEZONES.map(tz => (
                    <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Guests */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4" />
              Guests
            </Label>
            <Input
              type="number"
              min={1}
              value={formData.partySize}
              onChange={(e) => setFormData(prev => ({ ...prev, partySize: parseInt(e.target.value) || 1 }))}
              className="h-11 w-24"
            />
          </div>

          {/* Location */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MapPin className="w-4 h-4" />
              Location
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">City</Label>
                <Input
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="City"
                  className="h-11"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block">Country</Label>
                <Input
                  value={formData.country}
                  onChange={(e) => setFormData(prev => ({ ...prev, country: e.target.value }))}
                  placeholder="Country"
                  className="h-11"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Meeting Point</Label>
              <Input
                value={formData.pickupLocation}
                onChange={(e) => setFormData(prev => ({ ...prev, pickupLocation: e.target.value }))}
                placeholder="Where to meet the customer"
                className="h-11"
              />
            </div>
          </div>
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 2: CUSTOMER - Contact info, Emergency, Notes
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="customer" className="space-y-5 mt-0">
          {/* Customer Details */}
          <div className="space-y-3">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <User className="w-4 h-4" />
              Customer Details
            </Label>
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Name <span className="text-red-500">*</span></Label>
              <Input
                value={formData.customerName}
                onChange={(e) => setFormData(prev => ({ ...prev, customerName: e.target.value }))}
                placeholder="Full name"
                className="h-11"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                  <Mail className="w-3 h-3" /> Email
                </Label>
                <Input
                  type="email"
                  value={formData.customerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerEmail: e.target.value }))}
                  placeholder="email@example.com"
                  className="h-11"
                />
              </div>
              <div>
                <Label className="text-xs text-gray-500 mb-1 block flex items-center gap-1">
                  <Phone className="w-3 h-3" /> Phone
                </Label>
                <Input
                  value={formData.customerPhone}
                  onChange={(e) => setFormData(prev => ({ ...prev, customerPhone: e.target.value }))}
                  placeholder="+1 (555) 123-4567"
                  className="h-11"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <AlertCircle className="w-4 h-4 text-red-500" />
              Emergency Contact
            </Label>
            <Input
              value={formData.emergencyContactNumber}
              onChange={(e) => setFormData(prev => ({ ...prev, emergencyContactNumber: e.target.value }))}
              placeholder="Emergency contact phone"
              className="h-11"
            />
          </div>

          {/* Internal Notes */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4" />
              Internal Notes
            </Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Notes visible only to your team..."
              rows={3}
              className="resize-none min-h-[80px]"
            />
          </div>
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 3: ASSIGNMENT - Asset & Team
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="assignment" className="space-y-5 mt-0">
          {/* Asset Selection */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Anchor className="w-4 h-4" />
              Asset
            </Label>
            <Select 
              value={formData.assetId || 'unassigned'} 
              onValueChange={(value) => handleAssetChange(value)}
            >
              <SelectTrigger className="h-10">
                <SelectValue placeholder="Select Asset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="unassigned">— No Asset —</SelectItem>
                {assetsWithStatus.map(asset => (
                  <SelectItem 
                    key={asset.id} 
                    value={asset.id}
                    disabled={!asset.available}
                  >
                    <span className={!asset.available ? 'text-gray-400' : ''}>
                      {asset.name} {!asset.available && `(${asset.reason})`}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedAsset && (
              <p className="text-xs text-gray-500 mt-1">
                Capacity: {selectedAsset.capacity} • {selectedAsset.teamRequirements?.length || 0} team role(s)
              </p>
            )}
          </div>

          {/* Team Assignments */}
          <div>
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-2">
              <Users className="w-4 h-4" />
              Team
            </Label>
            {formData.teamAssignments && formData.teamAssignments.length > 0 ? (
              <div className="space-y-2">
                {formData.teamAssignments.map((assignment, idx) => {
                  const teamWithStatus = getTeamWithStatus(assignment.roleId);
                  const currentRole = roles.find(r => r.id === assignment.roleId);
                  const displayRoleName = currentRole?.name || assignment.roleName || 'Team Member';
                  const roleColor = currentRole?.color || '#6B7280';
                  
                  return (
                    <div key={`team-${idx}`} className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: roleColor }}
                      />
                      <span className="text-xs text-gray-600 w-20 truncate">{displayRoleName}</span>
                      <Select
                        value={assignment.teamId || 'unassigned'}
                        onValueChange={(value) => handleTeamChange(idx, value)}
                      >
                        <SelectTrigger className="flex-1 h-9 text-sm">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">— Unassigned —</SelectItem>
                          {teamWithStatus.map(member => (
                            <SelectItem 
                              key={member.id} 
                              value={member.id}
                              disabled={!member.available}
                            >
                              <span className={!member.available ? 'text-gray-400' : ''}>
                                {member.name} {!member.available && `(${member.reason})`}
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed">
                <Users className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  {formData.assetId ? 'No team roles required' : 'Select an asset first'}
                </p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 4: PAYMENT - Status, Amounts, Waiver, Check-in
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="payment" className="space-y-5 mt-0">
          {/* Payment Status & Check-in Row */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            {/* Status Dropdown */}
            <div className="flex items-center gap-2">
              <CreditCard className="w-4 h-4 text-gray-500" />
              <Select
                value={formData.paymentStatus || 'pending'}
                onValueChange={(val) => setFormData(prev => ({ ...prev, paymentStatus: val }))}
              >
                <SelectTrigger className={`w-32 h-9 text-xs font-medium border ${
                  formData.paymentStatus === 'paid' ? 'bg-green-50 border-green-200 text-green-700' :
                  formData.paymentStatus === 'partial' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' :
                  formData.paymentStatus === 'refunded' ? 'bg-gray-50 border-gray-200 text-gray-600' :
                  formData.paymentStatus === 'partially_refunded' ? 'bg-orange-50 border-orange-200 text-orange-700' :
                  'bg-red-50 border-red-200 text-red-700'
                }`}>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2 h-2 rounded-full ${
                      formData.paymentStatus === 'paid' ? 'bg-green-500' :
                      formData.paymentStatus === 'partial' ? 'bg-yellow-500' :
                      formData.paymentStatus === 'refunded' ? 'bg-gray-400' :
                      formData.paymentStatus === 'partially_refunded' ? 'bg-orange-500' :
                      'bg-red-500'
                    }`} />
                    <span>
                      {formData.paymentStatus === 'paid' ? 'Paid' :
                       formData.paymentStatus === 'partial' ? 'Partial' :
                       formData.paymentStatus === 'refunded' ? 'Refunded' :
                       formData.paymentStatus === 'partially_refunded' ? 'Part Refund' :
                       'Unpaid'}
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="paid">✓ Paid</SelectItem>
                  <SelectItem value="partial">⚠ Partial</SelectItem>
                  <SelectItem value="pending">Unpaid</SelectItem>
                  <SelectItem value="refunded">Refunded</SelectItem>
                  <SelectItem value="partially_refunded">Part Refund</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Check-in Toggle */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, checkedIn: !prev.checkedIn }))}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                formData.checkedIn 
                  ? 'bg-green-100 text-green-700 border border-green-200' 
                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              <Check className={`w-3.5 h-3.5 ${formData.checkedIn ? 'text-green-600' : 'text-gray-400'}`} />
              {formData.checkedIn ? 'Checked In' : 'Not Checked In'}
            </button>
          </div>

          {/* Past Booking Warning */}
          {isPastBooking && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700">This booking is in the past. Changes may affect records.</p>
            </div>
          )}

          {/* Price Breakdown */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-3">
            <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Price Breakdown</p>
            
            {/* Line items - show all fees for transparency */}
            <div className="space-y-1.5 text-sm">
              {/* Subtotal (base + add-ons) */}
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-900">${breakdown.subtotal.toFixed(2)}</span>
              </div>
              
              {/* Add-ons (if any) */}
              {breakdown.addOnsTotal > 0 && (
                <div className="flex justify-between text-xs pl-3">
                  <span className="text-gray-500">└ Includes add-ons</span>
                  <span className="text-gray-500">${breakdown.addOnsTotal.toFixed(2)}</span>
                </div>
              )}
              
              {/* Fees Section */}
              {(breakdown.platformFee > 0 || breakdown.stripeFee > 0 || breakdown.serviceFee > 0) && (
                <>
                  <div className="flex justify-between pt-1">
                    <span className="text-gray-600">Fees</span>
                    <span className="text-gray-900">${breakdown.totalFees.toFixed(2)}</span>
                  </div>
                  {breakdown.platformFee > 0 && (
                    <div className="flex justify-between text-xs pl-3">
                      <span className="text-gray-500">└ Platform fee</span>
                      <span className="text-gray-500">${breakdown.platformFee.toFixed(2)}</span>
                    </div>
                  )}
                  {breakdown.stripeFee > 0 && (
                    <div className="flex justify-between text-xs pl-3">
                      <span className="text-gray-500">└ Processing fee</span>
                      <span className="text-gray-500">${breakdown.stripeFee.toFixed(2)}</span>
                    </div>
                  )}
                  {breakdown.serviceFee > 0 && (
                    <div className="flex justify-between text-xs pl-3">
                      <span className="text-gray-500">└ Service fee</span>
                      <span className="text-gray-500">${breakdown.serviceFee.toFixed(2)}</span>
                    </div>
                  )}
                </>
              )}
              
              {/* Tax (if any) */}
              {breakdown.salesTax > 0 && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Tax {breakdown.taxRate > 0 && `(${breakdown.taxRate}%)`}</span>
                  <span className="text-gray-900">${breakdown.salesTax.toFixed(2)}</span>
                </div>
              )}
            </div>
            
            {/* Totals */}
            <div className="pt-3 border-t border-gray-200 space-y-2">
              <div className="flex justify-between text-sm font-semibold">
                <span className="text-gray-700">Total Due</span>
                <span className="text-gray-900">${totalDue.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Amount Paid</span>
                <span className={amountPaid > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}>
                  ${amountPaid.toFixed(2)}
                </span>
              </div>
              {remaining > 0.05 && (
                <div className="flex justify-between text-sm font-semibold">
                  <span className="text-red-600">Remaining</span>
                  <span className="text-red-600">${remaining.toFixed(2)}</span>
                </div>
              )}
              {remaining <= 0.05 && amountPaid > 0 && (
                <div className="flex items-center justify-center gap-2 pt-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-green-600">Fully Paid</span>
                </div>
              )}
            </div>
          </div>

          {/* Collect Payment Button */}
          {remaining > 0.05 && onGeneratePaymentLink && (
            <Button
              variant="outline"
              size="sm"
              className="w-full h-10 bg-orange-50 border-orange-200 text-orange-700 hover:bg-orange-100"
              onClick={() => {
                setGeneratingPaymentLink(true);
                onGeneratePaymentLink(trip).finally(() => setGeneratingPaymentLink(false));
              }}
              disabled={generatingPaymentLink}
            >
              {generatingPaymentLink ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Send Payment Link (${remaining.toFixed(2)})
            </Button>
          )}

          {/* Waiver Status */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-700">Waiver</span>
            </div>
            <Badge variant={trip.waiverSigned ? "default" : "secondary"} className="text-xs">
              {trip.waiverSigned ? '✓ Signed' : 'Pending'}
            </Badge>
          </div>
        </TabsContent>

        {/* ═══════════════════════════════════════════════════════════════════
            TAB 5: ADD-ONS - Manage add-ons & collect additional payment
        ═══════════════════════════════════════════════════════════════════ */}
        <TabsContent value="addons" className="space-y-5 mt-0">
          {/* Current Add-ons */}
          {formData.selectedAddOns.length > 0 && (
            <div>
              <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <Gift className="w-4 h-4" />
                Current Add-ons
              </Label>
              <div className="space-y-2">
                {formData.selectedAddOns.map((addon, idx) => (
                  <div 
                    key={`current-addon-${addon.id || idx}`} 
                    className="flex items-center justify-between p-3 rounded-lg bg-blue-50 border border-blue-200"
                  >
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{addon.name}</p>
                      <p className="text-xs text-gray-500">${(addon.price || 0).toFixed(2)} × {addon.quantity || 1}</p>
                    </div>
                    <span className="font-semibold text-gray-900">
                      ${((addon.price || 0) * (addon.quantity || 1)).toFixed(2)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Extras Section */}
          <div className="border-t pt-4">
            <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
              <Plus className="w-4 h-4" />
              Add Extras & Collect Payment
            </Label>
            
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              {/* Available Add-ons from Service */}
              {availableAddOns.filter(addon => !formData.selectedAddOns.some(s => s.id === addon.id || s.name === addon.name)).length > 0 && (
                <>
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500">Select add-ons to add:</p>
                    <div className="grid gap-2">
                      {availableAddOns
                        .filter(addon => !formData.selectedAddOns.some(s => s.id === addon.id || s.name === addon.name))
                        .map((addon) => {
                          const isSelected = newExtras.some(e => e.id === addon.id);
                          return (
                            <label
                              key={addon.id || addon.name}
                              className={`
                                flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all
                                ${isSelected 
                                  ? 'bg-blue-100 border border-blue-300' 
                                  : 'bg-white border border-gray-200 hover:border-blue-200'}
                              `}
                            >
                              <div className="flex items-center gap-3">
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={(e) => {
                                    if (e.target.checked) {
                                      setNewExtras(prev => [...prev, { ...addon, quantity: 1 }]);
                                    } else {
                                      setNewExtras(prev => prev.filter(ex => ex.id !== addon.id));
                                    }
                                  }}
                                  className="w-4 h-4 rounded text-blue-600"
                                />
                                <div>
                                  <p className="font-medium text-gray-900">{addon.name || addon.title}</p>
                                  {addon.description && (
                                    <p className="text-xs text-gray-500 line-clamp-1">{addon.description}</p>
                                  )}
                                </div>
                              </div>
                              <span className="font-semibold text-gray-900">
                                +${(addon.price || 0).toFixed(2)}
                              </span>
                            </label>
                          );
                        })}
                    </div>
                  </div>
                  <div className="relative">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-300"></div>
                    </div>
                    <div className="relative flex justify-center text-xs">
                      <span className="px-2 bg-gray-50 text-gray-500">or enter custom amount</span>
                    </div>
                  </div>
                </>
              )}
              
              {/* Custom Amount Input */}
              <div>
                <label className="text-xs text-gray-500 block mb-2">
                  Custom amount to collect:
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="0.00"
                    value={customExtraAmount}
                    onChange={(e) => setCustomExtraAmount(e.target.value)}
                    className="pl-7 h-11"
                  />
                </div>
              </div>

              {/* Show Payment Options when there's an amount */}
              {(newExtras.length > 0 || parseFloat(customExtraAmount) > 0) && (
                <>
                  {/* Total */}
                  <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
                    <span className="font-medium text-gray-700">Additional Amount</span>
                    <span className="text-lg font-bold text-blue-600">
                      ${(newExtras.reduce((sum, e) => sum + (e.price || 0) * (e.quantity || 1), 0) + parseFloat(customExtraAmount || 0)).toFixed(2)}
                    </span>
                  </div>

                  {/* Payment Method */}
                  <div>
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">How will they pay?</p>
                    <div className="grid grid-cols-2 gap-2 sm:gap-3">
                      {[
                        { id: 'pay_now_redirect', label: 'Pay Now', icon: CreditCard, color: 'blue' },
                        { id: 'send_link', label: 'Send Link', icon: Send, color: 'purple' },
                        { id: 'paid', label: 'Already Paid', icon: Check, color: 'green' },
                        { id: 'unpaid', label: 'Pay Later', icon: Clock, color: 'yellow' }
                      ].map((method) => {
                        const isSelected = extrasPaymentMethod === method.id;
                        const Icon = method.icon;
                        return (
                          <button
                            key={method.id}
                            type="button"
                            onClick={() => setExtrasPaymentMethod(method.id)}
                            className={`p-3 sm:p-4 border rounded-lg flex items-center gap-2 transition-all min-h-[48px] ${
                              isSelected 
                                ? `border-${method.color}-500 bg-${method.color}-50` 
                                : 'border-gray-200 hover:border-gray-300 bg-white active:bg-gray-50'
                            }`}
                            style={isSelected ? { 
                              borderColor: method.color === 'blue' ? '#3B82F6' : 
                                          method.color === 'purple' ? '#8B5CF6' : 
                                          method.color === 'green' ? '#22C55E' : '#EAB308',
                              backgroundColor: method.color === 'blue' ? '#EFF6FF' : 
                                              method.color === 'purple' ? '#F5F3FF' : 
                                              method.color === 'green' ? '#F0FDF4' : '#FEFCE8'
                            } : {}}
                          >
                            <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-gray-800' : 'text-gray-500'}`} />
                            <span className={`text-xs sm:text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-600'}`}>
                              {method.label}
                            </span>
                            {isSelected && <Check className="w-4 h-4 ml-auto text-gray-800 flex-shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Multi-Currency Dropdown - Only for Stripe methods */}
                  {(extrasPaymentMethod === 'pay_now_redirect' || extrasPaymentMethod === 'send_link') && supportedCurrencies.length > 0 && (
                    <div className="p-3 border border-gray-200 rounded-lg bg-gray-50">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-sm">💱</span>
                        <span className="text-sm font-medium text-gray-700">Customer&apos;s Currency</span>
                      </div>
                      <select
                        value={extrasCurrency || (trip.currency || 'USD').toLowerCase()}
                        onChange={(e) => setExtrasCurrency(e.target.value)}
                        className="w-full p-3 border rounded-lg bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm h-11"
                      >
                        <option value={(trip.currency || 'USD').toLowerCase()}>
                          {supportedCurrencies.find(c => c.code === (trip.currency || 'USD'))?.flag || '🏳️'} {(trip.currency || 'USD')} - Default (No conversion)
                        </option>
                        {supportedCurrencies
                          .filter(c => c.code.toLowerCase() !== (trip.currency || 'USD').toLowerCase())
                          .sort((a, b) => a.code.localeCompare(b.code))
                          .map(currency => (
                            <option key={currency.code} value={currency.code.toLowerCase()}>
                              {currency.flag} {currency.code} - {currency.name}
                            </option>
                          ))
                        }
                      </select>
                    </div>
                  )}

                  {/* Fee Notice for Stripe payments */}
                  {(extrasPaymentMethod === 'pay_now_redirect' || extrasPaymentMethod === 'send_link') && (
                    <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                      Processing fee (5.9% + $0.30) + applicable tax will be added at checkout.
                    </p>
                  )}

                  {/* Process Button */}
                  {extrasPaymentMethod && (
                    <Button
                      className="w-full h-12 text-base"
                      style={{ background: '#2563EB' }}
                      disabled={processingExtras}
                      onClick={async () => {
                        setProcessingExtras(true);
                        try {
                          const addOnsTotal = newExtras.reduce((sum, e) => sum + (e.price || 0) * (e.quantity || 1), 0);
                          const customAmount = parseFloat(customExtraAmount || 0);
                          const extrasTotal = addOnsTotal + customAmount;
                          
                          if (extrasPaymentMethod === 'paid') {
                            // Mark as paid - add extras to booking
                            if (newExtras.length > 0) {
                              setFormData(prev => ({
                                ...prev,
                                selectedAddOns: [...prev.selectedAddOns, ...newExtras]
                              }));
                            }
                            toast.success(`$${extrasTotal.toFixed(2)} collected - marked as paid`);
                            // Reset state
                            setNewExtras([]);
                            setExtrasPaymentMethod('');
                            setCustomExtraAmount('');
                          } else if (extrasPaymentMethod === 'unpaid') {
                            // Add to balance due
                            if (newExtras.length > 0) {
                              setFormData(prev => ({
                                ...prev,
                                selectedAddOns: [...prev.selectedAddOns, ...newExtras]
                              }));
                            }
                            toast.success(`$${extrasTotal.toFixed(2)} added to balance due`);
                            setNewExtras([]);
                            setExtrasPaymentMethod('');
                            setCustomExtraAmount('');
                          } else if (extrasPaymentMethod === 'send_link' || extrasPaymentMethod === 'pay_now_redirect') {
                            // Generate Stripe payment link
                            try {
                              const selectedCurrency = extrasCurrency || (trip.currency || 'USD').toLowerCase();
                              
                              // Add extras first
                              if (newExtras.length > 0) {
                                setFormData(prev => ({
                                  ...prev,
                                  selectedAddOns: [...prev.selectedAddOns, ...newExtras]
                                }));
                              }
                              
                              // Call API to create payment link/checkout session
                              const response = await api.post('/admin/bookings/create-extras-checkout', {
                                bookingId: trip.id,
                                confirmationCode: trip.confirmationCode || trip.confirmation_code,
                                amount: extrasTotal,
                                currency: selectedCurrency.toUpperCase(),
                                addOns: newExtras,
                                customAmount: customAmount,
                                paymentMethod: extrasPaymentMethod,
                                customerEmail: trip.customerEmail || trip.customer_email,
                                customerName: trip.customerName || trip.customer_name
                              });
                              
                              if (response.data?.checkoutUrl) {
                                // Show fee breakdown in toast
                                const fees = response.data.feeBreakdown;
                                
                                if (extrasPaymentMethod === 'pay_now_redirect') {
                                  // Redirect to Stripe checkout
                                  window.open(response.data.checkoutUrl, '_blank');
                                  const totalMsg = fees ? ` (Total: $${fees.grandTotal.toFixed(2)})` : '';
                                  toast.success(`Opening payment page...${totalMsg}`);
                                } else {
                                  // Copy link or show share modal
                                  navigator.clipboard?.writeText(response.data.checkoutUrl);
                                  const totalMsg = fees ? ` Customer will pay: $${fees.grandTotal.toFixed(2)}` : '';
                                  toast.success(`Payment link copied!${totalMsg}`);
                                }
                              } else if (response.data?.message) {
                                toast.success(response.data.message);
                              }
                              
                              setNewExtras([]);
                              setExtrasPaymentMethod('');
                              setCustomExtraAmount('');
                            } catch (err) {
                              console.error('Error creating payment link:', err);
                              toast.error(err.response?.data?.detail || 'Failed to create payment link');
                            }
                          }
                        } catch (err) {
                          console.error('Error processing extras:', err);
                          toast.error('Failed to process extras');
                        } finally {
                          setProcessingExtras(false);
                        }
                      }}
                    >
                      {processingExtras ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : extrasPaymentMethod === 'send_link' ? (
                        <Send className="w-4 h-4 mr-2" />
                      ) : extrasPaymentMethod === 'pay_now_redirect' ? (
                        <CreditCard className="w-4 h-4 mr-2" />
                      ) : (
                        <Check className="w-4 h-4 mr-2" />
                      )}
                      {extrasPaymentMethod === 'paid' ? 'Mark as Paid' :
                       extrasPaymentMethod === 'unpaid' ? 'Add to Balance' :
                       extrasPaymentMethod === 'send_link' ? 'Send Payment Link' :
                       extrasPaymentMethod === 'pay_now_redirect' ? 'Pay Now' : 'Confirm'}
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Custom Responses (Read-only) */}
          {trip.customAnswers && Object.keys(trip.customAnswers).length > 0 && (
            <div className="border-t pt-4">
              <Label className="flex items-center gap-2 text-sm font-medium text-gray-700 mb-3">
                <FileText className="w-4 h-4" />
                Custom Responses
              </Label>
              <div className="space-y-2 bg-gray-50 rounded-lg p-3">
                {Object.entries(trip.customAnswers).map(([question, answer], idx) => (
                  <div key={idx} className="text-sm">
                    <span className="text-gray-500">{question}:</span>{' '}
                    <span className="text-gray-900 font-medium">{String(answer)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </StandardModal>
  );
};

export default EditBookingModal;
